import { PushHandler } from './PushHandler';
import SurveyFactory from './SurveyFactory';
import * as Enums from '../common/Enumerations';
import { PushType } from '../common/EnumTypes';
import * as InternalTypes from '../common/InternalTypes';
import { LoggerTexts } from '../common/LoggerTexts';
import * as Types from '../common/Types';
import { Util } from '../common/Util';
import { CentralConfig } from '../config/CentralConfig';
import { AppContextDataContainer } from '../data/AppContextDataContainer';
import { ContextDataController } from '../data/ContextDataController';
import { PushContextData } from '../data/PushContextData';
import { QualtricsBridge } from '../QualtricsBridge';
import { StorageService } from '../storage/StorageService';

export class PresentationController {
	private _window: Window;
	private _storage: StorageService;
	private readonly _centralConfig: CentralConfig;
	private readonly _qualtricsBridge: QualtricsBridge;
	private readonly _contextDataController: ContextDataController;
	private _surveyInvitationDialogCallback: Types.SurveyInvitationDialogCallback;
	private _isDestroyCalled: boolean;
	private _isInvitationDialogOpen = false;
	private _pushHandler: PushHandler;

	constructor(
		storage: StorageService,
		centralConfig: CentralConfig,
		qualtricsBridge: QualtricsBridge,
		contextDataController: ContextDataController,
		surveyInvitationDialogCallback: Types.SurveyInvitationDialogCallback
	) {
		this._isDestroyCalled = false;
		this._window = window;
		if (!storage) {
			throw new Error(LoggerTexts.PRESENTATION_CTRL.CTOR_STORAGE_SRVC_NOT_PROVIDED);
		}
		if (!centralConfig) {
			throw new Error(LoggerTexts.PRESENTATION_CTRL.CTOR_CENTRAL_CFG_NOT_PROVIDED);
		}
		if (!qualtricsBridge) {
			throw new Error(LoggerTexts.PRESENTATION_CTRL.CTOR_QUALTRICS_BRIDGE_NOT_PROVIDED);
		}
		if (!contextDataController) {
			throw new Error(LoggerTexts.PRESENTATION_CTRL.CTOR_CNTXT_DATA_CTRL_NOT_PROVIDED);
		}
		if (!surveyInvitationDialogCallback) {
			throw new Error(LoggerTexts.PRESENTATION_CTRL.CTOR_INVITATION_CALLBACK_NOT_PROVIDED);
		}
		this._storage = storage;
		this._centralConfig = centralConfig;
		this._qualtricsBridge = qualtricsBridge;
		this._contextDataController = contextDataController;
		this._surveyInvitationDialogCallback = surveyInvitationDialogCallback;
		this._initializePushHandler();
	}

	public async displayInvitationDialog(
		clientAction: Enums.CLIENT_ACTION,
		pushContextData: PushContextData,
		stateConfig?: InternalTypes.StateConfig
	): Promise<boolean> {
		if (!this._isDestroyCalled) {
			this.pushHandler.stateConfig = stateConfig;
			if (this._checkIfSurveyInvitationDialogCanBeOpened(stateConfig)) {
				this.isInvitationDialogOpen = true;
				this._resetVariableContextData();
				const { appContextData, surveyUser } = await this._surveyInvitationDialogCallback(
					this._getSurveyInvitationDialogEventData(stateConfig)
				);
				this.isInvitationDialogOpen = false;
				this._setAppContext(AppContextDataContainer.create(appContextData, clientAction));
				if (surveyUser) {
					await this._surveyUser(pushContextData);
				} else {
					this.pushHandler.snoozePush();
				}
				return surveyUser;
			}
		}
		return false;
	}

	public destroy() {
		this._isDestroyCalled = true;
		this._pushHandler = undefined!;
		if (this._surveyInvitationDialogCallback) {
			this._surveyInvitationDialogCallback = undefined!;
		}
	}

	private _getSurveyInvitationDialogEventData(stateConfig?: InternalTypes.StateConfig): Types.SurveyInvitationDialogEventData {
		const pushType = this.pushHandler.getPushTypeForConsumers();
		const showInvitation = this._showInvitationDialog(pushType, stateConfig);
		return {
			pushType,
			showInvitation
		};
	}

	private _showInvitationDialog(pushType: PushType, stateConfig?: InternalTypes.StateConfig) {
		if (stateConfig && pushType === PushType.instantPush) {
			const instantPushConfig = this._centralConfig.pushConfig?.instantPushConfigs.get(stateConfig.combinedKey);
			if (instantPushConfig) {
				return instantPushConfig.showInvitation ?? true;
			}
		}
		return true;
	}

	private _initializePushHandler() {
		this._pushHandler = new PushHandler(this._storage, this._centralConfig, this._contextDataController);
	}

	private get pushHandler() {
		return this._pushHandler;
	}

	private set isInvitationDialogOpen(isInvitationDialogOpen: boolean) {
		this._isInvitationDialogOpen = isInvitationDialogOpen;
	}

	private get isInvitationDialogOpen() {
		return this._isInvitationDialogOpen;
	}

	private _checkIfSurveyInvitationDialogCanBeOpened(stateConfig?: InternalTypes.StateConfig): boolean {
		if (this.isInvitationDialogOpen) {
			return false;
		}
		if (!Util.isQualtricsSurveyPopupOpen()) {
			const pushType = this.pushHandler.getPushType();
			return this._canUserDigestSurvey(pushType, stateConfig);
		}
		return false;
	}

	private _canUserDigestSurvey(pushSrcType: Enums.PUSH_SRC_TYPE, stateConfig?: InternalTypes.StateConfig): boolean {
		const balancer = SurveyFactory.createBalancer();
		balancer.init(this._storage);
		return balancer.balance(pushSrcType, stateConfig);
	}

	private async _surveyUser(pushContextData: PushContextData) {
		this.pushHandler.setPushContextData(pushContextData);
		this.pushHandler.setPushTelemetryContextData();
		await this._openSurvey();
		this.pushHandler.updateLastPushTimestamp();
		this.pushHandler.updatePushState();
		this.pushHandler.updateNextPushDates();
	}

	private async _openSurvey() {
		if (this._qualtricsBridge.isApiLoaded) {
			this._window.QSI.API.unload();
			await this._window.QSI.API.load();
			this._window.QSI.API.run();
		}
	}

	private _resetVariableContextData() {
		this._contextDataController.resetVariableContextData();
	}

	private _setAppContext(appContextDataContainer: AppContextDataContainer) {
		this._contextDataController.setAppContext(appContextDataContainer);
	}
}
